import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyInput, parseMoney } from "@/components/MoneyInput";
import { api, formatApiError } from "@/lib/api";
import { moneyIn } from "@/lib/format";
import type { Account } from "@/lib/finance";

export type AssetAction = { kind: "revalue" | "dispose"; asset: Account } | null;
const today = () => new Date().toISOString().slice(0, 10);
const REASONS: [string, string][] = [["sale", "Venta"], ["trade_in", "Entrega a cuenta"], ["other", "Otro"]];

export function AssetActionDialog({ action, onClose, onSaved }: { action: AssetAction; onClose: () => void; onSaved: () => void }) {
  const [date, setDate] = useState(today());
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("trade_in");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (action) { setDate(today()); setValue(""); setReason("trade_in"); } }, [action]);
  if (!action) return null;
  const revalue = action.kind === "revalue";
  const save = async () => {
    setBusy(true);
    try {
      if (revalue) await api.post(`/assets/${action.asset.id}/revalue`, { value: parseMoney(value), date });
      else await api.post(`/assets/${action.asset.id}/dispose`, { date, reason });
      toast.success(revalue ? "Valor actualizado" : `${action.asset.name} dado de baja`);
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="asset-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">{revalue ? "Actualizar valor" : "Dar de baja"}: {action.asset.name}</DialogTitle>
          <DialogDescription>{revalue ? `Hoy vale ${moneyIn(action.asset.current_balance, action.asset.currency)}. Se registra un ajuste con la fecha que elijas.` : "Su valor pasa a $0 en esa fecha. Si lo vendiste por dinero, usa mejor el movimiento \u201cVenta de bien\u201d."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="asset-date" className="text-sm text-slate-700">Fecha</Label>
            <Input id="asset-date" type="date" value={date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDate(e.target.value)} className="h-11" data-testid="asset-action-date" />
          </div>
          {revalue ? (
            <div className="space-y-1.5">
              <Label htmlFor="asset-value" className="text-sm text-slate-700">Nuevo valor estimado</Label>
              <MoneyInput id="asset-value" value={value} onChange={setValue} className="h-11" testId="asset-action-value" />
            </div>
          ) : (
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="h-11" data-testid="asset-action-reason"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{REASONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button onClick={save} disabled={busy || (revalue && !value.trim())} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="asset-action-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : revalue ? "Guardar valor" : "Dar de baja"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
